import { type RefObject, useContext, useEffect, useMemo } from 'react';
import { useCustomBlock } from '@kizenapps/engine/react';
import { PluginContext } from 'ts-components/Plugins/context';
import {
  isCustomCodeContent,
  toEngineBlockConfig,
} from 'components/DashboardGrid/hooks/customCodeBlock';
import { blockViewsKey, registerBlockViews } from './blockViews';
import { createDashletRenderTracker } from 'ts-components/CustomBlockRender/dashletRenderRegistry';
import { BlockDashletWrapper } from '../Plugin/PluginBlockDashlet';
import { buildBlockArgs } from '../Plugin/blockArgs';

type CustomCodeBlockDashletProps = {
  dashlet: { id?: string; config?: { content?: unknown } };
  dateFilter?: any;
  teamFilter?: any;
  model?: { id?: string };
  [key: string]: any;
};

export const CustomCodeBlockDashlet = ({
  dashlet,
  dateFilter,
  teamFilter,
  model,
}: CustomCodeBlockDashletProps) => {
  const { sessionInvalid, registerInlinePages } = useContext(PluginContext);
  const dashletId = dashlet?.id ?? '';
  const content = dashlet?.config?.content;

  const block = useMemo(
    () =>
      isCustomCodeContent(content)
        ? toEngineBlockConfig(content, dashletId)
        : undefined,
    [content, dashletId]
  );

  const viewsKey = useMemo(
    () => blockViewsKey(content, dashletId),
    [content, dashletId]
  );

  useEffect(
    () => registerBlockViews(registerInlinePages, viewsKey, dashletId),
    [registerInlinePages, viewsKey, dashletId]
  );

  const objectId = model?.id;
  const args = useMemo(
    () => buildBlockArgs({ dateFilter, teamFilter, objectId }),
    [dateFilter, teamFilter, objectId]
  );

  const { scriptUIRef, scopedCss, pending } = useCustomBlock({
    block,
    args,
    instanceId: dashletId,
  });

  const renderTracker = useMemo(
    () => createDashletRenderTracker(dashletId),
    [dashletId]
  );
  useEffect(() => () => renderTracker.dispose(), [renderTracker]);
  useEffect(() => {
    renderTracker.contentCommitted(scriptUIRef.current);
  }, [renderTracker, scriptUIRef, block, args, sessionInvalid]);
  useEffect(() => {
    renderTracker.pendingChanged(pending);
  }, [renderTracker, pending]);

  if (sessionInvalid) {
    return null;
  }

  return (
    <BlockDashletWrapper>
      <div
        ref={scriptUIRef as RefObject<HTMLDivElement>}
        style={{ height: '100%', width: '100%' }}
      />
      <style>{scopedCss}</style>
    </BlockDashletWrapper>
  );
};
